<template>
    <DividerTitle :title="title" />
    <v-row class="d-flex align-center justify-flex-start">
        <!-- Select for Delivery Type (Grupaj / Expres) -->
        <v-col cols="12" sm="6" md="6" lg="3">
            <SwitchComponent
                v-model="truckTransportType"
                :leftTitle="$t('formFields.groupage')"
                :rightTitle="$t('formFields.express')"
            />
        </v-col>

        <!-- Vehicle coating: at least one of Prelată / Carosat required -->
        <v-col cols="12" sm="6" md="6" lg="2" data-cy="options-truck-coating">
            <div class="d-flex flex-column ga-0">
                <v-checkbox
                    v-model="curtainSidedChecked"
                    :label="$t('formFields.curtainSided')"
                    hide-details
                    density="compact"
                />
                <v-checkbox
                    v-model="boxTruckChecked"
                    :label="$t('formFields.boxTruck')"
                    hide-details
                    density="compact"
                />
            </div>
            <span v-if="truckCoatingErrorMessage" class="v-messages__message text-error text-caption d-block mt-1">
                {{ truckCoatingErrorMessage }}
            </span>
        </v-col>

        <!-- Platform Type (For Ware) -->
        <v-col cols="12" sm="5" md="4" lg="2" v-if="formType === 'ware'" data-cy="ware-options-platform-type">
            <v-select
                ref="platformType"
                v-model="selectedPlatformType"
                :items="platformTypeOptions"
                item-title="text"
                item-value="value"
                :label="$t('formFields.platformType')"
                v-auto-animate
                hide-details="auto"
                :rules="[requiredRulePlatform]"
                multiple
            />
        </v-col>
        <!-- Truck Type Selection -->
        <v-col cols="12" sm="6" md="6" lg="2" v-if="formType === 'truck'" data-cy="options-truck-type">
            <v-select
                ref="truckType"
                v-model="selectedTruckType"
                :items="truckTypeOptions"
                item-title="text"
                item-value="value"
                :label="$t('formFields.truckType')"
                v-auto-animate
                hide-details="auto"
                :rules="[requiredRule]"
            />
        </v-col>

        <v-col cols="12" sm="6" md="6" lg="2">
            <v-select
                v-model="selectedTruckSpecialFeatures"
                :items="truckSpecialFeatureOptions"
                item-title="text"
                item-value="value"
                :label="$t('formFields.specialFeatures')"
                clearable
                v-auto-animate
                multiple
                hide-details="auto"
            />
        </v-col>

        <v-col v-if="formType === 'ware'" cols="auto" lg="2">
            <v-checkbox
                v-model="escrowAccount"
                :label="$t('formFields.escrowAccount')"
                hide-details
            />
        </v-col>
    </v-row>
</template>

<script>
import {
    TRUCK_COATING,
    TRUCK_TRANSPORT_TYPE,
    TRUCK_TYPE,
    TRUCK_SPECIAL_FEATURE,
    PLATFORM_TYPE,
} from "@/enums/truckEnums.js";
import {mapState} from "vuex";
import DividerTitle from "./DividerTitle.vue";
import SwitchComponent from "./SwitchComponent.vue";

export default {
    props: {
        modelValue: Object,
        formType: String,
        title: String,
    },
    components: {
        DividerTitle,
        SwitchComponent,
    },
    emits: ["update:modelValue"],

    data() {
        return {
            requiredRule: (value) => !!value || this.$t("validation.required"),
            requiredRulePlatform: (value) =>
                value.length > 0 || this.$t("validation.required"),
        };
    },

    computed: {
        ...mapState(["isMobile"]),

        truckCoatingOptions() {
            return Object.values(TRUCK_COATING);
        },
        truckTransportTypeOptions() {
            return Object.values(TRUCK_TRANSPORT_TYPE);
        },

        truckTypeOptions() {
            return Object.values(TRUCK_TYPE).map((item) => ({
                value: item.value,
                text: this.$t(`transport.truckType.${item.value}`) || item.text,
            }));
        },
        truckSpecialFeatureOptions() {
            return Object.values(TRUCK_SPECIAL_FEATURE).map((item) => ({
                value: item.value,
                text:
                    this.$t(`transport.truckSpecialFeature.${item.value}`) ||
                    item.text,
            }));
        },
        platformTypeOptions() {
            return Object.values(PLATFORM_TYPE).map((item) => ({
                value: item.value,
                text:
                    this.$t(`transport.platformType.${item.value}`) ||
                    item.text,
            }));
        },
        // **Truck Transport Type Switch (Grupaj = false, Expres = true)**
        truckTransportType: {
            get() {
                return this.modelValue?.truckTransportType === 2; // 2 = Expres (true), 1 = Grupaj (false)
            },
            set(value) {
                this.$emit("update:modelValue", {
                    ...this.modelValue,
                    truckTransportType: value ? 2 : 1,
                });
            },
        },
        /** 1 = Prelată only, 2 = Carosat only, 3 = both */
        curtainSidedChecked: {
            get() {
                const value = this.modelValue?.truckCoating;
                return value === 1 || value === 3;
            },
            set(checked) {
                this.emitTruckCoating(checked, this.boxTruckChecked);
            },
        },
        boxTruckChecked: {
            get() {
                const value = this.modelValue?.truckCoating;
                return value === 2 || value === 3;
            },
            set(checked) {
                this.emitTruckCoating(this.curtainSidedChecked, checked);
            },
        },
        /** Shown when neither checkbox is selected; same style as other field errors. */
        truckCoatingErrorMessage() {
            const value = this.modelValue?.truckCoating;
            if (value === 1 || value === 2 || value === 3) return null;
            return this.$t("validation.truckCoatingRequired");
        },
        // Truck Type
        selectedTruckType: {
            get() {
                return this.modelValue?.truckType || null;
            },
            set(value) {
                this.$emit("update:modelValue", {
                    ...this.modelValue,
                    truckType: value,
                });
            },
        },
        // Multi-select computed property (array)
        selectedTruckSpecialFeatures: {
            get() {                       // returns [] if empty
                return this.modelValue?.truckSpecialFeatures ?? [];
            },
            set(val) {                    // val is an array of integers
                this.$emit("update:modelValue", {
                    ...this.modelValue,
                    truckSpecialFeatures: val
                });
            }
        },
        // Platform Type
        selectedPlatformType: {
            get() {
                return this.modelValue?.platformType || null;
            },
            set(value) {
                if (this.formType === "ware") {
                    this.$emit("update:modelValue", {
                        ...this.modelValue,
                        platformType: value,
                    });
                }
            },
        },
        escrowAccount: {
            get() {
                return !!this.modelValue?.escrowAccount;
            },
            set(value) {
                if (this.formType === "ware") {
                    this.$emit("update:modelValue", {
                        ...this.modelValue,
                        escrowAccount: value,
                    });
                }
            },
        },
    },
    methods: {
        /** Derive truck_coating from the two checkboxes: 1 = Prelată, 2 = Carosat, 3 = both */
        emitTruckCoating(curtainsided, boxtruck) {
            let value = null;
            if (curtainsided && boxtruck) value = 3;
            else if (curtainsided) value = 1;
            else if (boxtruck) value = 2;
            this.$emit("update:modelValue", {
                ...this.modelValue,
                truckCoating: value,
            });
        },
        /**
         * Manually triggers validation on all fields.
         * Returns true if all fields are valid, false otherwise.
         */
        async validateFields() {
            await this.$nextTick(); // Ensure reactivity updates before validation

            let isValid = true;

            const coating = this.modelValue?.truckCoating;
            if (coating !== 1 && coating !== 2 && coating !== 3) {
                isValid = false;
            }

            if (this.formType === "ware" && this.$refs.platformType) {
                const valid = await this.$refs.platformType.validate();
                if (this.$refs.platformType.errorMessages.length > 0)
                    isValid = false;
            }

            if (this.formType === "truck" && this.$refs.truckType) {
                const valid = await this.$refs.truckType.validate();

                if (this.$refs.truckType.errorMessages.length > 0)
                    isValid = false;
            }

            return isValid;
        },
        resetValidation() {
            if (this.$refs.truckType) this.$refs.truckType.resetValidation();
            if (this.$refs.platformType)
                this.$refs.platformType.resetValidation();
        },
        resetFields() {
            this.$emit("update:modelValue", {
                truckTransportType: 1,
                truckCoating: 1,
                platformType: [],
                truckType: "",
                truckSpecialFeatures: [],
                escrowAccount: false,
            });
        },
    },
};
</script>